import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { toast } from "sonner";
import { CheckCircle2, Circle, Loader2 } from "lucide-react";

const CAT_LABELS = {
  pt: {
    identity: "Identidade",
    credentials: "Credenciais",
    employment: "Emprego",
    evidence: "Evidências",
    medical: "Médico",
    family: "Família",
    financial: "Financeiro",
    biographic: "Biográfico",
    fees: "Taxas USCIS",
    underlying: "Petição Base",
    narrative: "Declaração",
    legal: "Jurídico",
    employer: "Empregador",
  },
  es: {
    identity: "Identidad",
    credentials: "Credenciales",
    employment: "Empleo",
    evidence: "Evidencias",
    medical: "Médico",
    family: "Familia",
    financial: "Financiero",
    biographic: "Biográfico",
    fees: "Tasas USCIS",
    underlying: "Petición Base",
    narrative: "Declaración",
    legal: "Legal",
    employer: "Empleador",
  },
};

export function ChecklistDialog({ open, onOpenChange, formId }) {
  const { t, lang } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState({});

  useEffect(() => {
    if (!open || !formId) return;
    setLoading(true);
    api.get(`/forms/${formId}/checklist`)
      .then((res) => {
        setData(res.data);
        try {
          const saved = JSON.parse(localStorage.getItem(`checklist-${formId}`) || "{}");
          setChecked(saved);
        } catch (_) {
          setChecked({});
        }
      })
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Failed to load checklist");
        onOpenChange(false);
      })
      .finally(() => setLoading(false));
  }, [open, formId, onOpenChange]);

  const toggle = (id) => {
    const next = { ...checked, [id]: !checked[id] };
    setChecked(next);
    try {
      localStorage.setItem(`checklist-${formId}`, JSON.stringify(next));
    } catch (_) { /* ignore */ }
  };

  const grouped = (data?.items || []).reduce((acc, it) => {
    (acc[it.category] = acc[it.category] || []).push(it);
    return acc;
  }, {});

  const totalRequired = (data?.items || []).filter((i) => i.required).length;
  const doneRequired = (data?.items || []).filter((i) => i.required && checked[i.id]).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="checklist-dialog"
        className="max-h-[85vh] overflow-y-auto rounded-none sm:max-w-2xl"
      >
        <DialogHeader>
          <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--primary))]">
            {t.checklist.overline} · {data?.form_code || "—"}
          </div>
          <DialogTitle className="text-3xl font-black tracking-tight">
            {t.checklist.title}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {data?.applicant_name && (
              <span className="font-mono text-[10px] uppercase tracking-[0.18em]">
                {data.applicant_role} · {data.applicant_name}
              </span>
            )}
            <span className="mt-2 block">{t.checklist.description}</span>
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {!loading && data && (
          <>
            <div
              data-testid="checklist-progress"
              className="hairline flex items-center justify-between bg-secondary/40 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em]"
            >
              <span className="text-muted-foreground">{t.checklist.progress}</span>
              <span className="text-[hsl(var(--primary))]">
                {doneRequired} / {totalRequired} {t.checklist.required_short}
              </span>
            </div>
            <div className="space-y-6">
              {Object.entries(grouped).map(([cat, items]) => (
                <div key={cat}>
                  <div className="font-mono text-[9px] uppercase tracking-[0.24em] text-muted-foreground">
                    {(CAT_LABELS[lang] || CAT_LABELS.pt)[cat] || cat}
                  </div>
                  <ul className="mt-2 space-y-2">
                    {items.map((it) => {
                      const isChecked = !!checked[it.id];
                      const label = lang === "pt" ? it.label_pt : it.label_es;
                      return (
                        <li
                          key={it.id}
                          className={`hairline flex items-start gap-3 p-3 ${
                            isChecked ? "bg-[hsl(var(--primary))]/[0.04]" : "bg-card"
                          }`}
                        >
                          <Checkbox
                            checked={isChecked}
                            onCheckedChange={() => toggle(it.id)}
                            data-testid={`checklist-item-${it.id}`}
                            className="mt-0.5"
                          />
                          <div className="flex-1">
                            <div className={`text-sm ${isChecked ? "text-muted-foreground line-through" : ""}`}>
                              {label}
                            </div>
                            {it.required && !isChecked && (
                              <span className="mt-1 inline-block font-mono text-[9px] uppercase tracking-[0.18em] text-[hsl(var(--warning))]">
                                {t.common.required}
                              </span>
                            )}
                          </div>
                          {isChecked ? (
                            <CheckCircle2 className="h-4 w-4 shrink-0 text-[hsl(var(--primary))]" />
                          ) : (
                            <Circle className="h-4 w-4 shrink-0 text-muted-foreground" />
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
